/**
 * SuperAdmin — Plan Change Modal (Task 10.SA2)
 * PATCH /api/superadmin/tenants/:id/plan
 * ConfirmModal fires before PATCH. isSubmitting lock prevents double-tap.
 * reason is required — canSubmit guards the Save button until filled.
 */

import React, { useState } from 'react';
import { changeTenantPlan } from '../../utils/api';

const PLAN_OPTIONS = ['FREE', 'STARTER', 'AI_ADMIN', 'SCALE'];

const PLAN_LABELS = {
  FREE:     'Free',
  STARTER:  'Starter',
  AI_ADMIN: 'AI Admin',
  SCALE:    'Scale',
};

const PLAN_BADGE = {
  FREE:     { bg: '#f5f5f5',               color: '#666'    },
  STARTER:  { bg: '#e3f2fd',               color: '#1565c0' },
  AI_ADMIN: { bg: 'rgba(76,227,205,0.18)', color: '#0a8a76' },
  SCALE:    { bg: '#f3e5f5',               color: '#6a1b9a' },
};

const S = {
  overlay:    { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 500,
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' },
  modal:      { background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '420px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },
  header:     { padding: '20px 24px 16px', borderBottom: '1px solid #f0f0f0',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title:      { fontSize: '15px', fontWeight: '700', color: '#1a1a2e' },
  closeBtn:   { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px',
                color: '#aaa', lineHeight: 1, padding: '4px', minWidth: '32px', minHeight: '32px' },
  body:       { padding: '20px 24px' },
  row:        { marginBottom: '16px' },
  label:      { display: 'block', fontSize: '11px', fontWeight: '700', color: '#888',
                textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' },
  badgeRow:   { display: 'flex', alignItems: 'center', gap: '8px' },
  badge:      (plan) => ({
                display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
                fontSize: '11px', fontWeight: '700',
                background: (PLAN_BADGE[plan] || PLAN_BADGE.FREE).bg,
                color:      (PLAN_BADGE[plan] || PLAN_BADGE.FREE).color }),
  select:     { width: '100%', padding: '9px 12px', borderRadius: '7px',
                border: '1px solid #e0e0e0', fontSize: '13px', color: '#1a1a2e',
                background: '#fff', boxSizing: 'border-box', outline: 'none' },
  hint:       { fontSize: '12px', color: '#888', marginTop: '8px', lineHeight: '1.5' },
  errorBanner:{ background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)',
                borderRadius: '7px', padding: '10px 14px', fontSize: '12px',
                color: '#e74c3c', marginBottom: '14px' },
  footer:     { padding: '16px 24px', borderTop: '1px solid #f0f0f0',
                display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  cancelBtn:  { padding: '9px 18px', borderRadius: '7px', border: '1px solid #e0e0e0',
                background: '#fff', fontSize: '13px', fontWeight: '600', color: '#555',
                cursor: 'pointer', minHeight: '38px' },
  submitBtn:  (disabled) => ({
                padding: '9px 20px', borderRadius: '7px', border: 'none',
                background: disabled ? '#b2dfdb' : '#4CE3CD',
                color:      disabled ? '#fff'    : '#0D0F1F',
                fontSize: '13px', fontWeight: '700',
                cursor: disabled ? 'not-allowed' : 'pointer',
                minHeight: '38px', opacity: disabled ? 0.7 : 1 }),
  reasonInput: {
    width: '100%', padding: '9px 12px', borderRadius: '7px',
    border: '1px solid #e0e0e0', fontSize: '13px', color: '#1a1a2e',
    background: '#fff', boxSizing: 'border-box', outline: 'none',
    resize: 'vertical', fontFamily: 'inherit', minHeight: '72px',
  },
  required: { color: 'inherit', marginLeft: '2px' },

  // ConfirmModal — nested overlay, z-index above main modal
  confirmOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                    zIndex: 600, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', padding: '16px' },
  confirmBox:     { background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '380px',
                    padding: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  confirmTitle:   { fontSize: '14px', fontWeight: '700', color: '#1a1a2e', marginBottom: '10px' },
  confirmBody:    { fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '20px' },
  confirmActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px' },
};

// ─── ConfirmModal ─────────────────────────────────────────────────────────────
const ConfirmModal = ({ tenantName, fromPlan, toPlan, reason, onConfirm, onCancel, isSubmitting }) => (
  <div style={S.confirmOverlay}>
    <div style={S.confirmBox}>
      <div style={S.confirmTitle}>Change plan?</div>
      <div style={S.confirmBody}>
        Change <strong>{tenantName}</strong> plan from{' '}
        <strong>{PLAN_LABELS[fromPlan] || fromPlan}</strong> to{' '}
        <strong>{PLAN_LABELS[toPlan] || toPlan}</strong>?{' '}
        Reason: <em>{reason}</em>
      </div>
      <div style={S.confirmActions}>
        <button type="button" style={S.cancelBtn} onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="button" style={S.submitBtn(isSubmitting)} onClick={onConfirm} disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Confirm change'}
        </button>
      </div>
    </div>
  </div>
);

// ─── PlanChangeModal ──────────────────────────────────────────────────────────
const PlanChangeModal = ({ tenant, onClose, onChanged }) => {
  const [selectedPlan, setSelectedPlan] = useState(tenant.plan);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error,        setError]        = useState(null);
  const [reason,       setReason]       = useState('');

  const canSubmit = selectedPlan !== tenant.plan && reason.trim() !== '' && !isSubmitting;

  const handleSaveClick = () => {
    setError(null);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await changeTenantPlan(tenant._id, selectedPlan, reason.trim());
      setShowConfirm(false);
      onChanged();
    } catch (err) {
      setShowConfirm(false);
      setError(err.response?.data?.message || 'Failed to change plan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelConfirm = () => {
    if (!isSubmitting) setShowConfirm(false);
  };

  return (
    <div style={S.overlay}>
      {showConfirm && (
        <ConfirmModal
          tenantName={tenant.tenantName}
          fromPlan={tenant.plan}
          toPlan={selectedPlan}
          reason={reason}
          onConfirm={handleConfirm}
          onCancel={handleCancelConfirm}
          isSubmitting={isSubmitting}
        />
      )}

      <div style={S.modal}>
        <div style={S.header}>
          <span style={S.title}>Change Plan</span>
          <button type="button" style={S.closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={S.body}>
          {error && <div style={S.errorBanner}>{error}</div>}

          <div style={S.row}>
            <label style={S.label}>Current plan</label>
            <div style={S.badgeRow}>
              <span style={S.badge(tenant.plan)}>
                {PLAN_LABELS[tenant.plan] || tenant.plan}
              </span>
              <span style={{ fontSize: '12px', color: '#aaa' }}>{tenant.tenantName}</span>
            </div>
          </div>

          <div style={S.row}>
            <label style={S.label}>New plan</label>
            <select
              style={S.select}
              value={selectedPlan}
              onChange={e => setSelectedPlan(e.target.value)}
              disabled={isSubmitting}
            >
              {PLAN_OPTIONS.map(p => (
                <option key={p} value={p}>{PLAN_LABELS[p]}</option>
              ))}
            </select>
            <div style={S.hint}>
              Changing plan takes effect immediately. skillActivated features update automatically.
            </div>
          </div>

          <div style={S.row}>
            <label style={S.label}>
              Reason for change <span style={S.required}>*</span>
            </label>
            <textarea
              style={S.reasonInput}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Explain why the plan is being changed…"
              rows={3}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div style={S.footer}>
          <button type="button" style={S.cancelBtn} onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            style={S.submitBtn(!canSubmit)}
            onClick={handleSaveClick}
            disabled={!canSubmit}
          >
            Save plan
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanChangeModal;
